import { fromEntries } from "../utils/browserCompat";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Collapse, Paper, Snackbar, Stack, Typography } from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import NotificationsOffRoundedIcon from "@mui/icons-material/NotificationsOffRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import { subscribeStorefrontPush, unsubscribeStorefrontPush } from "../api/push";
import { getStorefrontPushSubscription, storefrontWebPushSupported, subscribeStorefrontBrowser, unsubscribeStorefrontBrowser } from "../utils/webPushClient";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { useLanding } from "../context/LandingContext";
import { getStorefrontIdentity, storefrontIdentityKey, subscribeStorefrontIdentity } from "../config/storefrontRuntime";
import { WEB_PUSH_PROMPT_STYLE_DEFAULTS } from "../utils/webPushPromptVisualOwnership";

const LEGACY_DISMISSED_KEY = "webprofit:webpush-prompt-dismissed";
function dismissedStorageKey(identity = getStorefrontIdentity()) { return `webprofit:${storefrontIdentityKey(identity)}:webpush-prompt-dismissed:v1`; }
function readDismissedPreference(identity = getStorefrontIdentity()) {
  if (typeof window === "undefined") return false;
  try {
    const scopedKey = dismissedStorageKey(identity);
    const scopedValue = window.localStorage.getItem(scopedKey);
    if (scopedValue !== null) return scopedValue === "1";
    if (identity?.resolutionMode !== "domain") return false;
    const legacyValue = window.localStorage.getItem(LEGACY_DISMISSED_KEY);
    if (legacyValue !== "1") return false;
    try { window.localStorage.setItem(scopedKey, "1"); window.localStorage.removeItem(LEGACY_DISMISSED_KEY); } catch {}
    return true;
  } catch { return false; }
}
function persistDismissedPreference(identity = getStorefrontIdentity()) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(dismissedStorageKey(identity), "1"); } catch {}
}

const DEFAULT_PROMPT = Object.freeze({
  enabled: true,
  title: "Recibí novedades",
  subscribedTitle: "Notificaciones activadas",
  description: "Activá los avisos de {businessName} para enterarte de novedades y promociones.",
  subscribedDescription: "Este navegador recibe avisos de {businessName}.",
  enableLabel: "Activar notificaciones",
  disableLabel: "Desactivar",
  dismissLabel: "Ahora no",
  processingLabel: "Procesando…",
  successMessage: "Notificaciones activadas correctamente.",
  disableSuccessMessage: "Notificaciones desactivadas.",
  errorMessage: "No se pudieron activar las notificaciones.",
  disableErrorMessage: "No se pudieron desactivar las notificaciones.",
  position: "bottom-right",
});
function copyValue(value, fallback, businessName) {
  const source = value === undefined || value === null ? fallback : String(value);
  return source.replace(/\{businessName\}/g, businessName);
}
function actionSx(style, disabledStyle = null) {
  const disabled = disabledStyle || style;
  return {
    bgcolor: style.backgroundColor,
    color: style.textColor,
    border: `${style.borderWidth}px solid ${style.borderColor}`,
    boxShadow: style.boxShadow || "none",
    borderRadius: `${style.borderRadius}px`,
    "&:hover": { bgcolor: style.hoverBackgroundColor, color: style.hoverTextColor, borderColor: style.hoverBorderColor },
    "&.Mui-focusVisible": { outline: `3px solid ${style.focusRingColor}`, outlineOffset: 2 },
    "&.Mui-disabled": { bgcolor: disabled.backgroundColor, color: disabled.textColor, borderColor: disabled.borderColor, opacity: 1 },
  };
}

export default function WebPushPrompt() {
  const { webPushEnabled, webPush, project } = useProjectConfig();
  const { notifications } = useLanding();
  const promptSettings = notifications?.webPushPrompt || {};
  const style = promptSettings.style || WEB_PUSH_PROMPT_STYLE_DEFAULTS;
  const [supported] = useState(storefrontWebPushSupported);
  const [subscribed, setSubscribed] = useState(false);
  const [dismissed, setDismissed] = useState(() => readDismissedPreference());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const permission = supported ? Notification.permission : "unsupported";
  const visible = webPushEnabled && promptSettings?.enabled !== false && supported && !dismissed && permission !== "denied";

  useEffect(() => subscribeStorefrontIdentity((identity) => setDismissed(readDismissedPreference(identity))), []);
  useEffect(() => {
    if (!webPushEnabled || !supported) return undefined;
    let cancelled = false;
    getStorefrontPushSubscription().then((subscription) => { if (!cancelled) setSubscribed(Boolean(subscription)); }).catch(() => { if (!cancelled) setSubscribed(false); });
    return () => { cancelled = true; };
  }, [supported, webPushEnabled]);

  const businessName = useMemo(() => project?.name || "este comercio", [project?.name]);
  const copy = useMemo(() => fromEntries(Object.entries(DEFAULT_PROMPT).map(([key, fallback]) => [key, typeof fallback === "string" ? copyValue(promptSettings?.[key], fallback, businessName) : promptSettings?.[key] ?? fallback])), [businessName, promptSettings]);

  const handleSubscribe = async () => {
    setBusy(true); setError("");
    try {
      const subscription = await subscribeStorefrontBrowser(webPush?.publicKey);
      await subscribeStorefrontPush(subscription.toJSON(), { campaigns: true });
      setSubscribed(true); setMessage(copy.successMessage);
    } catch (requestError) {
      setError(copy.errorMessage || requestError?.response?.data?.message || requestError?.message || DEFAULT_PROMPT.errorMessage);
    } finally { setBusy(false); }
  };
  const handleUnsubscribe = async () => {
    setBusy(true); setError("");
    try {
      const result = await unsubscribeStorefrontBrowser();
      if (result.endpoint) await unsubscribeStorefrontPush(result.endpoint);
      setSubscribed(false); setMessage(copy.disableSuccessMessage);
    } catch (requestError) {
      setError(copy.disableErrorMessage || requestError?.response?.data?.message || requestError?.message || DEFAULT_PROMPT.disableErrorMessage);
    } finally { setBusy(false); }
  };
  const handleDismiss = () => { persistDismissedPreference(); setDismissed(true); };
  const placeLeft = copy.position === "bottom-left";
  const mainStyle = subscribed ? style.disableButton : style.enableButton;
  const titleStyle = subscribed ? style.subscribedTitle : style.title;
  const descriptionStyle = subscribed ? style.subscribedDescription : style.description;

  return (
    <>
      <Collapse in={visible}>
        <Paper elevation={0} sx={{ position: "fixed", zIndex: (theme) => theme.zIndex.snackbar, ...(placeLeft ? { left: { xs: 12, sm: 20 } } : { right: { xs: 12, sm: 20 } }), bottom: { xs: 12, sm: 20 }, width: { xs: "calc(100% - 24px)", sm: 390 }, p: 2, bgcolor: style.surface.backgroundColor, border: `${style.surface.borderWidth}px solid ${style.surface.borderColor}`, borderRadius: `${style.surface.borderRadius}px`, boxShadow: style.surface.boxShadow }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ width: 42, height: 42, borderRadius: `${style.icon.borderRadius}px`, border: `${style.icon.borderWidth}px solid ${style.icon.borderColor}`, display: "grid", placeItems: "center", bgcolor: style.icon.surfaceBackgroundColor, color: style.icon.color, flexShrink: 0 }}>
              <NotificationsActiveRoundedIcon sx={{ color: "inherit" }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, color: titleStyle.color }}>{subscribed ? copy.subscribedTitle : copy.title}</Typography>
              <Typography variant="body2" sx={{ mt: 0.35, color: descriptionStyle.color }}>{subscribed ? copy.subscribedDescription : copy.description}</Typography>
              {error ? (
                <Alert severity="error" icon={<ErrorOutlineRoundedIcon sx={{ color: style.error.iconColor }} />} sx={{ mt: 1, bgcolor: style.error.backgroundColor, color: style.error.textColor, border: `${style.error.borderWidth}px solid ${style.error.borderColor}`, borderRadius: `${style.error.borderRadius}px`, "& .MuiAlert-icon": { color: style.error.iconColor }, "& .MuiAlert-message": { color: style.error.textColor } }}>{error}</Alert>
              ) : null}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button size="small" startIcon={subscribed ? <NotificationsOffRoundedIcon /> : <NotificationsActiveRoundedIcon />} onClick={subscribed ? handleUnsubscribe : handleSubscribe} disabled={busy} sx={actionSx(mainStyle, style.processing)}>
                  {busy ? copy.processingLabel : subscribed ? copy.disableLabel : copy.enableLabel}
                </Button>
                <Button size="small" startIcon={<CloseRoundedIcon />} onClick={handleDismiss} disabled={busy} sx={actionSx(style.dismissButton)}>{copy.dismissLabel}</Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Collapse>
      <Snackbar open={Boolean(message)} autoHideDuration={3500} onClose={() => setMessage("")}>
        <Paper role="status" sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, bgcolor: style.success.backgroundColor, color: style.success.textColor, border: `${style.success.borderWidth}px solid ${style.success.borderColor}`, borderRadius: `${style.success.borderRadius}px`, boxShadow: style.success.boxShadow }}>
          <CheckCircleOutlineRoundedIcon sx={{ color: style.success.iconColor }} />
          <Typography variant="body2" sx={{ color: "inherit", fontWeight: 700 }}>{message}</Typography>
        </Paper>
      </Snackbar>
    </>
  );
}
